import type React from 'react';
import { format, subDays } from 'date-fns';

import { DateInput, DatePicker, FormField, FormHelp, Input, Label, Textarea } from '@zillow/constellation-10';

import type { Section } from '../../../models';
import { FIRE_ALARM } from '../../../constants';
import YesAndNoExpansion from '../../../../shared/components/Widgets/YesAndNoExpansion';
import { DATE_PICKER_FORMAT } from '../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function FireAlarm({ section, setSectionValues }: Props) {
  function onNoFireAlarm(): void {
    setSectionValues({ [FIRE_ALARM.FIELD_NAME.FIRE_ALARM]: false });
  }

  function onYesFireAlarm(): void {
    setSectionValues({ [FIRE_ALARM.FIELD_NAME.FIRE_ALARM]: true });
  }

  function setFireAlarmPullStationLocationDescription(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.FIRE_ALARM_PULL_STATION_LOCATION]: event.currentTarget.value,
    });
  }

  function setFireAlarmControlLocationDescription(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.FIRE_ALARM_CONTROL_PANEL_LOCATION]: event.currentTarget.value,
    });
  }

  function onFireAlarmControlPanelServiceDateChange(formattedDate: string): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.FIRE_ALARM_CONTROL_PANEL_SERVICE_DATE]: formattedDate,
    });
  }

  function setULCertificationNumber(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.UL_CERITIFICATION_NUMBER]: event.currentTarget.value,
    });
  }

  function onULExpirationChange(formattedDate: string): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.UL_EXPIRATION_DATE]: formattedDate,
    });
  }

  return (
    <FormField
      required
      label={<Label>Is there fire alarm system in the building?</Label>}
      control={
        <YesAndNoExpansion
          yesHandler={onYesFireAlarm}
          noHandler={onNoFireAlarm}
          yesActive={section.getFieldValue(FIRE_ALARM.FIELD_NAME.FIRE_ALARM)}
        >
          <FormField
            required
            css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }}
            label={<Label>Describe the location of the fire alarm pull stations on the tenant’s floor</Label>}
            control={
              <Textarea
                css={{ resize: 'vertical' }}
                onChange={setFireAlarmPullStationLocationDescription}
                value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.FIRE_ALARM_PULL_STATION_LOCATION)}
              />
            }
          />
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>Describe the location of the fire alarm control panel in building</Label>}
            control={
              <Textarea
                css={{ resize: 'vertical' }}
                onChange={setFireAlarmControlLocationDescription}
                value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.FIRE_ALARM_CONTROL_PANEL_LOCATION)}
              />
            }
          />
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>Fire alarm control panel service date</Label>}
            control={
              <DatePicker
                dateFormat={DATE_PICKER_FORMAT}
                value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.FIRE_ALARM_CONTROL_PANEL_SERVICE_DATE)}
                onValueChange={(value) => {
                  const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
                  onFireAlarmControlPanelServiceDateChange(formatted);
                }}
                maxDate={new Date()}
                closeModalOnDateSelect
                input={<DateInput />}
              />
            }
          />
          <FormField
            css={{ marginBottom: 'layout.tight' }}
            optional
            label={<Label id="service-provider">UL certification #</Label>}
            control={
              <Input
                onChange={setULCertificationNumber}
                value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.UL_CERITIFICATION_NUMBER)}
                aria-labelledby="service-provider"
                data-test="service-provider"
              />
            }
            description={
              <FormHelp>
                Fire alarm systems should be certified by an organization that is part of the OSHA Nationally Recognized
                Testing Laboratory Program, such as UL. An exception is made for fire-warning systems and fire alarm
                systems in three-family apartment buildings.
              </FormHelp>
            }
          />
          <FormField
            optional
            label={<Label>UL expiration date</Label>}
            control={
              <DatePicker
                dateFormat={DATE_PICKER_FORMAT}
                value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.UL_EXPIRATION_DATE)}
                onValueChange={(value) => {
                  const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
                  onULExpirationChange(formatted);
                }}
                minDate={subDays(new Date(), 1)}
                closeModalOnDateSelect
                input={<DateInput />}
              />
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}

FireAlarm.description = 'Asks about fire alarm features within a building';

export default FireAlarm;
